import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, Button, Badge } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import {
  LEAD_TYPE_FORM_OPTIONS,
  LEAD_SOURCE_FORM_OPTIONS,
} from '../../lib/leadMeta';

const ADMIN_ROLES = new Set(['admin', 'super_admin']);

function MultiSelectChips({ label, options, selected, onChange }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? 'bg-primary text-white border-primary'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-primary/40'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ExclusivityTab({ onPendingCountChange }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const agencyId = user?.agency_id;
  const isAdmin = ADMIN_ROLES.has(user?.role);

  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveDays, setApproveDays] = useState('14');
  const [approveLoading, setApproveLoading] = useState(false);
  const [denyLoadingId, setDenyLoadingId] = useState(null);

  const [bulkTypes, setBulkTypes] = useState([]);
  const [bulkSources, setBulkSources] = useState([]);
  const [bulkCount, setBulkCount] = useState(null);
  const [bulkCountLoading, setBulkCountLoading] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);

  const loadData = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const [pendingRows, historyRows, count] = await Promise.all([
        api.exclusivity.getPendingRequests(agencyId),
        api.exclusivity.getResolvedRequests(agencyId),
        api.exclusivity.getPendingRequestCount(agencyId),
      ]);
      setPending(pendingRows || []);
      setHistory(historyRows || []);
      onPendingCountChange?.(count ?? 0);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load exclusivity data', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [agencyId, addToast, onPendingCountChange]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const refreshBulkCount = useCallback(async () => {
    if (!agencyId || !isAdmin) return;
    setBulkCountLoading(true);
    try {
      const count = await api.exclusivity.countForBulkNonExclusive(agencyId, {
        leadTypes: bulkTypes,
        leadSources: bulkSources,
      });
      setBulkCount(count);
    } catch {
      setBulkCount(null);
    } finally {
      setBulkCountLoading(false);
    }
  }, [agencyId, isAdmin, bulkTypes, bulkSources]);

  useEffect(() => {
    void refreshBulkCount();
  }, [refreshBulkCount]);

  const handleApprove = async () => {
    const days = parseInt(approveDays, 10);
    if (!approveTarget || !days || days < 1) {
      addToast({ type: 'error', title: 'Invalid timeframe', message: 'Enter days (1 or more).' });
      return;
    }
    setApproveLoading(true);
    try {
      await api.exclusivity.approveRequest(approveTarget.id, days);
      addToast({ type: 'success', title: 'Request approved' });
      setApproveTarget(null);
      await loadData();
    } catch (err) {
      addToast({ type: 'error', title: 'Approve failed', message: err.message });
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDeny = async (requestId) => {
    setDenyLoadingId(requestId);
    try {
      await api.exclusivity.denyRequest(requestId);
      addToast({ type: 'success', title: 'Request denied' });
      await loadData();
    } catch (err) {
      addToast({ type: 'error', title: 'Deny failed', message: err.message });
    } finally {
      setDenyLoadingId(null);
    }
  };

  const handleBulkApply = async () => {
    if (!agencyId) return;
    setBulkApplying(true);
    try {
      const result = await api.exclusivity.bulkSetNonExclusive(agencyId, {
        leadTypes: bulkTypes,
        leadSources: bulkSources,
      });
      addToast({
        type: 'success',
        title: 'Bulk update complete',
        message: `${result.updated} lead(s) set to non-exclusive.`,
      });
      setBulkConfirmOpen(false);
      await refreshBulkCount();
    } catch (err) {
      addToast({ type: 'error', title: 'Bulk update failed', message: err.message });
    } finally {
      setBulkApplying(false);
    }
  };

  const typeOptions = LEAD_TYPE_FORM_OPTIONS.filter((o) => o.value !== 'recruit');
  const sourceOptions = LEAD_SOURCE_FORM_OPTIONS;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading exclusivity settings…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Pending requests</h2>
        <p className="text-sm text-gray-500 mb-4">Review agent requests for lead exclusivity.</p>

        {pending.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No pending requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-500">Lead</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Requested by</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Reason</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Requested at</th>
                  <th className="px-4 py-3 font-medium text-gray-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100 align-top">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.lead_name}</td>
                    <td className="px-4 py-3 text-gray-700">{row.requester_name}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs whitespace-pre-wrap">{row.reason}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {row.created_at
                        ? format(parseISO(row.created_at), 'MMM d, yyyy h:mm a')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="primary" onClick={() => setApproveTarget(row)}>
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void handleDeny(row.id)}
                          disabled={denyLoadingId === row.id}
                          isLoading={denyLoadingId === row.id}
                        >
                          Deny
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Request history</h2>
        <p className="text-sm text-gray-500 mb-4">Recently approved and denied requests.</p>

        {history.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-4 text-center">No resolved requests yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-500">Lead</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Requested by</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Resolved by</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.lead_name}</td>
                    <td className="px-4 py-3 text-gray-700">{row.requester_name}</td>
                    <td className="px-4 py-3">
                      <Badge variant={row.status === 'approved' ? 'success' : 'danger'}>
                        {row.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.approver_name}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {row.created_at
                        ? format(parseISO(row.created_at), 'MMM d, yyyy')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {isAdmin && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Bulk set non-exclusive</h2>
          <p className="text-sm text-gray-500 mb-4">
            Mark matching active leads as non-exclusive so any agent can work them.
          </p>

          <div className="space-y-4 max-w-2xl">
            <MultiSelectChips
              label="Lead types (optional)"
              options={typeOptions}
              selected={bulkTypes}
              onChange={setBulkTypes}
            />
            <MultiSelectChips
              label="Lead sources (optional)"
              options={sourceOptions}
              selected={bulkSources}
              onChange={setBulkSources}
            />

            <p className="text-sm text-gray-700">
              {bulkCountLoading ? (
                <span className="text-gray-500">Counting matches…</span>
              ) : (
                <>
                  <span className="font-semibold">{bulkCount ?? 0}</span> lead(s) match these filters
                </>
              )}
            </p>

            <Button
              variant="primary"
              size="sm"
              disabled={bulkCountLoading || !bulkCount}
              onClick={() => setBulkConfirmOpen(true)}
            >
              Set as Non-Exclusive
            </Button>
          </div>
        </Card>
      )}

      <Modal
        isOpen={!!approveTarget}
        onClose={() => !approveLoading && setApproveTarget(null)}
        title="Approve exclusivity request"
        className="max-w-md"
      >
        {approveTarget && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Grant exclusivity to <span className="font-medium">{approveTarget.requester_name}</span> for{' '}
              <span className="font-medium">{approveTarget.lead_name}</span>.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Timeframe (days)</label>
            <input
              type="number"
              min={1}
              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm mb-4"
              value={approveDays}
              onChange={(e) => setApproveDays(e.target.value)}
              disabled={approveLoading}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setApproveTarget(null)} disabled={approveLoading}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={() => void handleApprove()} disabled={approveLoading}>
                {approveLoading ? (
                  <>
                    <Spinner className="mr-2" />
                    Approving…
                  </>
                ) : (
                  'Approve'
                )}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={bulkConfirmOpen}
        onClose={() => !bulkApplying && setBulkConfirmOpen(false)}
        title="Confirm bulk update"
        className="max-w-md"
      >
        <p className="text-sm text-gray-600 mb-4">
          Set <span className="font-semibold">{bulkCount ?? 0}</span> matching lead(s) to non-exclusive?
          Existing exclusive assignments will be cleared.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setBulkConfirmOpen(false)} disabled={bulkApplying}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={() => void handleBulkApply()} disabled={bulkApplying}>
            {bulkApplying ? (
              <>
                <Spinner className="mr-2" />
                Updating…
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
